/**
 * User list page — FE-03.
 * Search, filter by status/plan, paginated table, click to detail.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AdminUserSummary,
  type UserStatus,
  fetchUsers,
} from "../../api/admin-users";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = ["all", "active", "frozen", "deactivated"] as const;
const PLAN_OPTIONS = ["all", "free", "pro", "premium", "max"] as const;

export function UserListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [plan, setPlan] = useState<string>("all");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchUsers({
        q: query || undefined,
        status: status === "all" ? undefined : (status as UserStatus),
        plan: plan === "all" ? undefined : plan,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("user_detail.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [query, status, plan, offset]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleSearch = useCallback((): void => {
    setOffset(0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") handleSearch();
    },
    [handleSearch],
  );

  const handleFilterChange = useCallback((): void => {
    setOffset(0);
  }, []);

  return (
    <div>
      <PageHeader
        title={t("users.title")}
        description={t("users.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        query={query}
        status={status}
        plan={plan}
        onQueryChange={setQuery}
        onStatusChange={(v) => {
          setStatus(v);
          handleFilterChange();
        }}
        onPlanChange={(v) => {
          setPlan(v);
          handleFilterChange();
        }}
        onKeyDown={handleKeyDown}
        onSearch={handleSearch}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("users.loading")}</p>
      ) : (
        <>
          <UserTable
            items={items}
            onRowClick={(id) => navigate(`/users/${id}`)}
          />
          <Pagination offset={offset} total={total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}

// ─── FilterBar ───

type FilterBarProps = {
  query: string;
  status: string;
  plan: string;
  onQueryChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onPlanChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearch: () => void;
};

function FilterBar(p: FilterBarProps): React.JSX.Element {
  return (
    <div style={s.filterBar}>
      <input
        type="text"
        placeholder={t("users.search_placeholder")}
        value={p.query}
        onChange={(e) => p.onQueryChange(e.target.value)}
        onKeyDown={p.onKeyDown}
        style={s.searchInput}
      />
      <select
        value={p.status}
        onChange={(e) => p.onStatusChange(e.target.value)}
        style={s.select}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("users.all_status") : capitalize(v)}
          </option>
        ))}
      </select>
      <select
        value={p.plan}
        onChange={(e) => p.onPlanChange(e.target.value)}
        style={s.select}
        aria-label="Filter by plan"
      >
        {PLAN_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("users.all_plans") : capitalize(v)}
          </option>
        ))}
      </select>
      <button type="button" onClick={p.onSearch} style={s.searchBtn}>
        {t("common.search")}
      </button>
    </div>
  );
}

// ─── Table ───

const COLUMN_KEYS = [
  "users.col_name",
  "users.col_email",
  "users.col_custom_id",
  "users.col_plan",
  "users.col_tokens",
  "users.col_storage",
  "users.col_status",
  "users.col_created",
] as const;

function UserTable(props: {
  items: AdminUserSummary[];
  onRowClick: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0)
    return <p style={s.empty}>{t("users.no_users_found")}</p>;
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            {COLUMN_KEYS.map((c) => (
              <th key={c} style={s.th}>
                {t(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.items.map((u) => (
            <UserRow key={u.id} user={u} onClick={props.onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow(props: {
  user: AdminUserSummary;
  onClick: (id: string) => void;
}): React.JSX.Element {
  const { user: u } = props;
  return (
    <tr
      style={{ ...s.tr, cursor: "pointer" }}
      onClick={() => props.onClick(u.id)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") props.onClick(u.id);
      }}
    >
      <td style={s.td}>{u.display_name}</td>
      <td style={s.td}>{u.email}</td>
      <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
        {u.custom_id ?? "-"}
      </td>
      <td style={s.td}>{capitalize(u.plan_type)}</td>
      <td style={s.tdRight}>{u.token_balance.toLocaleString()}</td>
      <td style={s.tdRight}>
        {formatStorage(u.storage_used_bytes, u.storage_limit_bytes)}
      </td>
      <td style={s.td}>
        <StatusBadge status={u.status} />
      </td>
      <td style={s.td}>{formatDate(u.created_at)}</td>
    </tr>
  );
}

// ─── Pagination ───

function Pagination(props: {
  offset: number;
  total: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const page = Math.floor(props.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("users.page_info", { total: props.total, page, totalPages })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.offset === 0}
          onClick={() => props.onChange(Math.max(0, props.offset - PAGE_SIZE))}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={props.offset + PAGE_SIZE >= props.total}
          onClick={() => props.onChange(props.offset + PAGE_SIZE)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── StatusBadge ───

function StatusBadge({ status }: { status: UserStatus }): React.JSX.Element {
  const color = STATUS_COLOR[status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {capitalize(status)}
    </span>
  );
}

const STATUS_COLOR: Record<UserStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  frozen: { bg: "#fef2f2", fg: "#991b1b" },
  deactivated: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatStorage(used: number, limit: number): string {
  const fmt = (b: number): string => {
    if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
    if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
    return `${(b / 1_024).toFixed(0)} KB`;
  };
  return limit > 0 ? `${fmt(used)} / ${fmt(limit)}` : fmt(used);
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  filterBar: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: "8px 12px",
    fontSize: 13,
    border: "1px solid var(--color-border)",
    borderRadius: 6,
  },
  select: {
    padding: "8px 12px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  searchBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  loading: { fontSize: 14, color: "var(--color-text-secondary)", padding: 24 },
  error: { fontSize: 14, color: "var(--color-danger)", padding: "8px 0" },
  empty: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    padding: 24,
    textAlign: "center",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
  },
  tdRight: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  tr: { cursor: "pointer" },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 0",
  },
  pageInfo: { fontSize: 13, color: "var(--color-text-secondary)" },
  pageButtons: { display: "flex", gap: 8 },
  pageBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
};
